import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { ArrowLeft, Send, Loader2, MessageSquare, Users, Plus, Check, X, Bell, BellOff, Camera, Search, HeartHandshake, Pin, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { messagesApi, connectionsApi } from '../services/messagesApi';
import { postsApi, mediaUrl, renderBadgeImage } from '../services/postsApi';
import Confetti from './Confetti';
import PrayerPanels from './messages-inbox/PrayerPanels';
import PrayerWall from './messages-inbox/PrayerWall';
import NewGroupDialog from './messages-inbox/NewGroupDialog';

const MILESTONES = [7, 30, 100];

const MessagesInbox = ({ open, onClose, language = 'en', currentUserId, focus = null }) => {
  const { t, language: ctxLang } = useLanguage();
  const es = (language || ctxLang) === 'es';
  const [tab, setTab] = useState('chats'); // chats | requests
  const [view, setView] = useState('list'); // list | thread
  const [threads, setThreads] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState([]);
  const [groupOpen, setGroupOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [prayerMode, setPrayerMode] = useState(false);
  const [convoTab, setConvoTab] = useState('chat');
  const [wall, setWall] = useState(null);
  const [streak, setStreak] = useState(null);
  const [celebrate, setCelebrate] = useState(null);
  const [shareNudge, setShareNudge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [partner, setPartner] = useState(null);
  const [partnerPickerOpen, setPartnerPickerOpen] = useState(false);
  const [partnerPeople, setPartnerPeople] = useState([]);
  const scrollRef = useRef(null);
  const leaderboardRef = useRef(null);
  const typingPingRef = useRef(0);

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const [th, r] = await Promise.all([messagesApi.threads(), messagesApi.requests()]);
      setThreads(th); setRequests(r);
    } catch { setThreads([]); setRequests([]); }
    finally { setLoading(false); }
  }, []);

  // Refresh the streak after praying; celebrate on a milestone.
  const bumpStreak = () => {
    messagesApi.prayerStreak().then((s) => {
      if (s?.freeze_just_used) {
        toast(t('messages.freezeSaved'), { duration: 7000 });
      }
      setStreak((prev) => {
        if (s && MILESTONES.includes(s.current_streak) && (!prev || prev.current_streak !== s.current_streak)) {
          setCelebrate({ days: s.current_streak, shared: false });
        }
        return s;
      });
    }).catch(() => {});
    const prevShared = partner?.shared_streak;
    messagesApi.getPartner().then((p) => {
      setPartner(p);
      if (p?.status === 'active' && MILESTONES.includes(p.shared_streak) && p.shared_streak !== prevShared) {
        setCelebrate({ days: p.shared_streak, shared: true, partnerName: p.partner_name });
      }
    }).catch(() => {});
  };

  const invitePartner = async (targetId) => {
    try {
      const res = await messagesApi.invitePartner(targetId);
      setPartner(res.partnership);
      toast.success(t('messages.inviteSent'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('messages.somethingWrong')); }
  };

  const respondPartner = async (accept) => {
    try {
      await messagesApi.respondPartner(partner.id, accept);
      messagesApi.getPartner().then(setPartner).catch(() => setPartner(null));
      toast.success(accept ? t('messages.nowPartners') : t('messages.inviteDeclined'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('messages.error')); }
  };

  const endPartner = async () => {
    try { await messagesApi.endPartner(partner.id); setPartner(null); toast.success(t('messages.partnershipEnded')); }
    catch (e) { toast.error(t('messages.error')); }
  };

  const messagePartner = async () => {
    try {
      const res = await messagesApi.partnerChat(partner.id);
      openThread(res.thread_id);
    } catch (e) { toast.error(t('messages.couldNotOpenChat')); }
  };

  const openPartnerPicker = () => {
    setPartnerPickerOpen(true);
    connectionsApi.friends()
      .then((f) => connectionsApi.following().then((fo) => setPartnerPeople([...f, ...fo.filter((x) => !f.some((y) => y.user_id === x.user_id))])))
      .catch(() => setPartnerPeople([]));
  };

  const toggleLeaderboardPrivacy = async () => {
    const next = !(streak?.opt_out);
    try {
      await messagesApi.setLeaderboardPrivacy(next);
      setStreak((s) => ({ ...(s || {}), opt_out: next }));
      messagesApi.streakLeaderboard().then(setLeaderboard).catch(() => {});
      toast.success(next ? t('messages.hiddenFromLeaderboard') : t('messages.visibleOnLeaderboard'));
    } catch { toast.error(t('messages.somethingWrong')); }
  };

  const shareMilestone = async () => {
    const c = celebrate;
    setCelebrate(null);
    if (!c) return;
    const blob = await renderBadgeImage(c.days, c.shared ? 'partner' : 'personal', c.partnerName || '', es);
    window.dispatchEvent(new CustomEvent('lumera:milestone-share', { detail: {
      kind: 'streak',
      direct: true,
      imageBlob: blob,
      message: t('messages.shareBadgePrompt'),
    }}));
  };

  useEffect(() => { if (open) { setView('list'); setTab('chats'); setActive(null); loadLists(); messagesApi.prayerStreak().then(setStreak).catch(() => setStreak(null)); messagesApi.streakLeaderboard().then(setLeaderboard).catch(() => setLeaderboard([])); messagesApi.getPartner().then(setPartner).catch(() => setPartner(null)); } }, [open, loadLists]);

  // Deep-link: scroll the streak leaderboard into view when opened via a Pulse tap.
  useEffect(() => {
    if (open && focus === 'leaderboard' && view === 'list' && leaderboard.length > 0) {
      const timer = setTimeout(() => leaderboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
      return () => clearTimeout(timer);
    }
  }, [open, focus, view, leaderboard.length]);

  // Deep-link: open a specific 1-on-1 thread when launched via "Message" on a profile.
  useEffect(() => {
    if (open && focus && typeof focus === 'object' && focus.openThreadId) {
      openThread(focus.openThreadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focus]);

  useEffect(() => {
    if (view === 'thread' && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages, view, typing]);

  // Poll for new messages + typing while a thread is open.
  useEffect(() => {
    if (view !== 'thread' || !active?.thread_id) return;
    const id = active.thread_id;
    const poll = setInterval(async () => {
      try {
        const [data, typ] = await Promise.all([messagesApi.messages(id), messagesApi.getTyping(id)]);
        setActive((a) => (a && a.thread_id === id ? { ...a, messages: data.messages, header: data.header } : a));
        setTyping(typ);
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(poll);
  }, [view, active?.thread_id]);

  const openThread = async (threadId) => {
    setView('thread'); setActive(null); setTyping([]); setPrayerMode(false); setConvoTab('chat'); setWall(null);
    try { setActive(await messagesApi.messages(threadId)); loadLists(); }
    catch { toast.error(t('messages.couldNotOpenThread')); setView('list'); }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    const body = text.trim(); setText('');
    try {
      if (prayerMode) {
        const res = await messagesApi.addPrayer(active.thread_id, body);
        setActive((a) => ({ ...a, messages: [...a.messages, res.message], pinned_prayers: [...(a.pinned_prayers || []), res.message] }));
        setPrayerMode(false);
      } else {
        const res = await messagesApi.send(active.thread_id, body);
        setActive((a) => ({ ...a, messages: [...a.messages, res.message] }));
      }
    } catch { toast.error(t('messages.couldNotSend')); setText(body); }
    finally { setSending(false); }
  };

  const pray = async (messageId) => {
    try {
      const res = await messagesApi.pray(messageId);
      if (res.praying) bumpStreak();
      setActive((a) => {
        const upd = (m) => m.id === messageId ? { ...m, i_prayed: res.praying, pray_count: res.pray_count } : m;
        return { ...a, messages: a.messages.map(upd), pinned_prayers: (a.pinned_prayers || []).map(upd) };
      });
    } catch { toast.error(t('messages.somethingWrong')); }
  };

  const markAnswered = async (messageId) => {
    try {
      await messagesApi.setAnswered(messageId, true);
      toast.success(t('messages.prayerAnsweredToast'));
      const prayerMsg = (active?.messages || []).find((m) => m.id === messageId) || (wall?.open || []).find((m) => m.id === messageId);
      setActive((a) => a ? ({
        ...a,
        messages: a.messages.map((m) => m.id === messageId ? { ...m, answered: true } : m),
        pinned_prayers: (a.pinned_prayers || []).filter((m) => m.id !== messageId),
      }) : a);
      // Offer to share the answered prayer as a testimony to the main feed.
      // Shown as an in-Sheet banner (never a global toast) so the Share action
      // is always reachable and not trapped behind the Sheet overlay.
      const txt = prayerMsg?.text;
      if (txt) setShareNudge({ text: txt });
    } catch (e) { toast.error(e?.response?.data?.detail || t('messages.error')); }
  };

  const shareTestimony = () => {
    const txt = shareNudge?.text;
    setShareNudge(null);
    window.dispatchEvent(new CustomEvent('lumera:milestone-share', { detail: {
      kind: 'testimony', direct: true,
      reference: t('messages.answeredPrayerRef'),
      verseText: txt, message: t('messages.shareTestimonyPrompt'),
    }}));
  };

  const loadWall = async () => {
    if (!active) return;
    setWall(null);
    try { setWall(await messagesApi.threadPrayers(active.thread_id)); } catch { setWall({ open: [], answered: [], total_prayers_offered: 0 }); }
  };
  const prayWall = async (id) => {
    try { const res = await messagesApi.pray(id); if (res.praying) bumpStreak(); setWall((w) => ({ ...w, open: w.open.map((p) => p.id === id ? { ...p, i_prayed: res.praying, pray_count: res.pray_count } : p), total_prayers_offered: w.total_prayers_offered + (res.praying ? 1 : -1) })); } catch { toast.error(t('messages.error')); }
  };
  const answerWall = async (id) => { await markAnswered(id); loadWall(); };

  const togglePin = async (message) => {
    try {
      const res = await messagesApi.setPin(message.id, !message.pinned);
      setActive((a) => {
        const msgs = a.messages.map((m) => m.id === message.id ? { ...m, pinned: res.pinned } : m);
        return { ...a, messages: msgs, pinned_messages: msgs.filter((m) => m.pinned && m.kind !== 'prayer') };
      });
      toast.success(res.pinned ? t('messages.messagePinned') : t('messages.unpinnedToast'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('messages.error')); }
  };

  // Close the inbox when a testimony share (or any global flow) needs the
  // create-post Dialog to take over — prevents Sheet overlay from blocking it.
  useEffect(() => {
    const closeHandler = () => { if (open) onClose(); };
    window.addEventListener('lumera:close-inbox', closeHandler);
    return () => window.removeEventListener('lumera:close-inbox', closeHandler);
  }, [open, onClose]);


  // Debounced conversation search across all threads.
  useEffect(() => {
    if (!open || view !== 'list') return;
    if (query.trim().length < 2) { setResults(null); return; }
    const timer = setTimeout(async () => {
      try { setResults(await messagesApi.search(query.trim())); } catch { setResults([]); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, open, view]);

  const onType = (v) => {
    setText(v);
    const now = Date.now();
    if (active?.thread_id && now - typingPingRef.current > 2000) {
      typingPingRef.current = now;
      messagesApi.setTyping(active.thread_id).catch(() => {});
    }
  };

  const accept = async (id) => { try { await messagesApi.acceptRequest(id); toast.success(t('messages.accepted')); loadLists(); openThread(id); } catch { toast.error(t('messages.error')); } };
  const decline = async (id) => { try { await messagesApi.declineRequest(id); setRequests((r) => r.filter((th) => th.id !== id)); } catch { toast.error(t('messages.error')); } };

  const toggleMute = async () => {
    if (!active) return;
    const muted = active.header?.muted;
    try {
      if (muted) await messagesApi.unmute(active.thread_id); else await messagesApi.mute(active.thread_id);
      setActive((a) => ({ ...a, header: { ...a.header, muted: !muted } }));
      toast.success(muted ? t('messages.unmuted') : t('messages.muted'));
    } catch { toast.error(t('messages.error')); }
  };

  const avatarRef = useRef(null);
  const changeAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !active) return;
    try {
      const up = await postsApi.uploadImage(f, f.name);
      await messagesApi.setGroupAvatar(active.thread_id, up.image_url);
      setActive((a) => ({ ...a, header: { ...a.header, image_url: up.image_url } }));
      toast.success(t('messages.photoUpdated'));
    } catch (e2) { toast.error(e2?.response?.data?.detail || t('messages.error')); }
  };

  const isGroup = active?.header?.type === 'group';
  const title = view === 'thread'
    ? (isGroup ? active?.header?.name : active?.header?.other_name) || t('messages.message')
    : t('messages.title');

  // index of my last sent message (for read receipt on direct threads)
  const myLastIdx = active ? (() => { for (let i = active.messages.length - 1; i >= 0; i--) if (active.messages[i].sender_id === currentUserId) return i; return -1; })() : -1;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="messages-inbox">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            {view === 'thread' && (
              <button onClick={() => { setView('list'); loadLists(); }} data-testid="messages-back-btn"><ArrowLeft className="w-5 h-5" /></button>
            )}
            <span className="flex items-center gap-2">{isGroup && view === 'thread' && <Users className="w-4 h-4" />}{title}</span>
            {view === 'thread' && (
              <span className="ml-auto mr-8 flex items-center gap-1">
                {isGroup && active?.header?.is_admin && (
                  <button onClick={() => avatarRef.current?.click()} className="text-muted-foreground p-1.5" data-testid="messages-group-avatar-btn" title={t('messages.changePhoto')}><Camera className="w-4 h-4" /></button>
                )}
                <button onClick={toggleMute} className="text-muted-foreground p-1.5" data-testid="messages-mute-btn" title={active?.header?.muted ? t('messages.unmute') : t('messages.mute')}>
                  {active?.header?.muted ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                </button>
                <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={changeAvatar} data-testid="messages-group-avatar-input" />
              </span>
            )}
            {view === 'list' && (
              <button onClick={() => setGroupOpen(true)} className="ml-auto mr-8 text-primary" data-testid="messages-new-group-btn" title={t('messages.newGroup')}><Plus className="w-5 h-5" /></button>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">{t('messages.directAndGroup')}</SheetDescription>
          {shareNudge && (
            <div className="absolute bottom-0 inset-x-0 z-40 m-3 rounded-xl border bg-background shadow-lg p-3 flex items-center gap-3" data-testid="testimony-share-nudge">
              <PartyPopper className="w-5 h-5 text-green-600 shrink-0" />
              <p className="text-sm flex-1 leading-tight">{t('messages.shareTestimonyWithCommunity')}</p>
              <Button size="sm" onClick={shareTestimony} className="bg-green-600 hover:bg-green-700" data-testid="testimony-share-btn">{t('messages.share')}</Button>
              <button onClick={() => setShareNudge(null)} className="text-muted-foreground hover:text-foreground" data-testid="testimony-share-dismiss"><X className="w-4 h-4" /></button>
            </div>
          )}
          {celebrate && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-sm px-6" data-testid="streak-celebration">
              <Confetti />
              <div className="text-6xl animate-bounce">🔥</div>
              <p className="mt-3 text-3xl font-extrabold">{celebrate.days} {t('messages.days')}</p>
              <p className="text-sm text-muted-foreground mb-6 text-center">
                {celebrate.shared
                  ? t('messages.sharedStreakFaithful', { name: celebrate.partnerName })
                  : t('messages.prayerStreakFaithful')}
              </p>
              <Button onClick={shareMilestone} className="w-full max-w-xs bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600" data-testid="streak-share-btn">
                <PartyPopper className="w-4 h-4 mr-2" /> {t('messages.shareMyMilestone')}
              </Button>
              <Button variant="ghost" onClick={() => setCelebrate(null)} className="mt-2 w-full max-w-xs" data-testid="streak-dismiss-btn">
                {t('messages.keepPraying')}
              </Button>
            </div>
          )}
          {view === 'thread' && isGroup && (
            <p className="text-xs text-muted-foreground">{t('messages.membersCount', { count: active?.header?.members_count })}</p>
          )}
        </SheetHeader>

        {view === 'list' ? (
          <>
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('messages.searchPlaceholder')} className="pl-9 rounded-full h-9" data-testid="messages-search-input" />
              </div>
            </div>
            <PrayerPanels
              streak={streak}
              partner={partner}
              leaderboard={leaderboard}
              partnerPickerOpen={partnerPickerOpen}
              setPartnerPickerOpen={setPartnerPickerOpen}
              partnerPeople={partnerPeople}
              openPartnerPicker={openPartnerPicker}
              invitePartner={invitePartner}
              respondPartner={respondPartner}
              endPartner={endPartner}
              messagePartner={messagePartner}
              toggleLeaderboardPrivacy={toggleLeaderboardPrivacy}
              leaderboardRef={leaderboardRef}
            />
            {results !== null ? (
              <div className="flex-1 overflow-y-auto" data-testid="messages-search-results">
                {results.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-16 px-6">{t('messages.noResults')}</p>
                ) : results.map((r, i) => (
                  <button key={i} onClick={() => { setQuery(''); setResults(null); openThread(r.thread_id); }} className="w-full text-left p-4 border-b hover:bg-muted/50" data-testid="messages-search-result">
                    <p className="text-xs font-semibold text-primary">{r.label}</p>
                    <p className="text-sm truncate">{r.text}</p>
                  </button>
                ))}
              </div>
            ) : (
            <>
            <div className="flex border-b">
              <button onClick={() => setTab('chats')} className={`flex-1 py-2.5 text-sm font-medium ${tab === 'chats' ? 'border-b-2 border-primary' : 'text-muted-foreground'}`} data-testid="messages-tab-chats">{t('messages.chats')}</button>
              <button onClick={() => setTab('requests')} className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 ${tab === 'requests' ? 'border-b-2 border-primary' : 'text-muted-foreground'}`} data-testid="messages-tab-requests">
                {t('messages.requests')}
                {requests.length > 0 && <span className="bg-rose-500 text-white text-[10px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center">{requests.length}</span>}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : tab === 'requests' ? (
                requests.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-16 px-6">{t('messages.noMessageRequests')}</p>
                ) : requests.map((t2) => (
                  <div key={t2.id} className="flex items-center gap-3 p-4 border-b" data-testid={`messages-request-${t2.id}`}>
                    <Avatar className="w-11 h-11">
                      {t2.other_picture ? <AvatarImage src={t2.other_picture} /> : null}
                      <AvatarFallback className="bg-primary/15">{(t2.other_name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{t2.other_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{t2.last_message}</p>
                    </div>
                    <button onClick={() => accept(t2.id)} className="text-green-600 p-1.5" data-testid={`messages-accept-${t2.id}`}><Check className="w-5 h-5" /></button>
                    <button onClick={() => decline(t2.id)} className="text-rose-500 p-1.5" data-testid={`messages-decline-${t2.id}`}><X className="w-5 h-5" /></button>
                  </div>
                ))
              ) : threads.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground px-6">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">{t('messages.noMessagesYet')}</p>
                </div>
              ) : threads.map((t2) => (
                <button key={t2.id} onClick={() => openThread(t2.id)} className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 text-left border-b" data-testid={`messages-thread-${t2.id}`}>
                  {t2.type === 'group' ? (
                    t2.image_url ? (
                      <img src={mediaUrl(t2.image_url)} alt="" className="w-11 h-11 rounded-full object-cover" />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center"><Users className="w-5 h-5 text-primary" /></div>
                    )
                  ) : (
                    <Avatar className="w-11 h-11">
                      {t2.other_picture ? <AvatarImage src={t2.other_picture} /> : null}
                      <AvatarFallback className="bg-primary/15">{(t2.other_name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate flex items-center gap-1 ${t2.unread_count ? 'font-bold' : 'font-medium'}`}>
                      {t2.type === 'group' ? t2.name : t2.other_name}
                      {t2.muted && <BellOff className="w-3 h-3 text-muted-foreground shrink-0" data-testid={`messages-muted-${t2.id}`} />}
                    </p>
                    <p className={`text-xs truncate ${t2.unread_count ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {t2.type === 'group' && t2.pinned_preview
                        ? <span className="flex items-center gap-1" data-testid={`thread-pinned-${t2.id}`}><Pin className="w-3 h-3 shrink-0" /> {t2.pinned_preview}</span>
                        : (t2.last_message || (t2.type === 'group' ? (t2.member_names || []).join(', ') : ''))}
                    </p>
                  </div>
                  {t2.unread_count > 0 && <span className="bg-primary text-primary-foreground text-[10px] rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">{t2.unread_count}</span>}
                </button>
              ))}
            </div>
            </>
            )}
          </>
        ) : (
          <>
            {isGroup && (
              <div className="flex border-b shrink-0">
                <button onClick={() => setConvoTab('chat')} className={`flex-1 py-2 text-sm font-medium ${convoTab === 'chat' ? 'border-b-2 border-primary' : 'text-muted-foreground'}`} data-testid="convo-tab-chat">{t('messages.chat')}</button>
                <button onClick={() => { setConvoTab('wall'); loadWall(); }} className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 ${convoTab === 'wall' ? 'border-b-2 border-primary' : 'text-muted-foreground'}`} data-testid="convo-tab-wall"><HeartHandshake className="w-4 h-4" /> {t('messages.prayerWall')}</button>
              </div>
            )}
            {convoTab === 'wall' ? (
              <PrayerWall wall={wall} prayWall={prayWall} answerWall={answerWall} />
            ) : (
            <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {active && (active.pinned_prayers || []).length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-xl p-3 space-y-2" data-testid="pinned-prayers">
                  <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5 uppercase tracking-wide"><HeartHandshake className="w-3.5 h-3.5" /> {t('messages.prayerRequests')}</p>
                  {active.pinned_prayers.map((p) => (
                    <div key={p.id} className="flex items-center gap-2">
                      <p className="text-sm flex-1 break-words">{p.text}</p>
                      <button onClick={() => pray(p.id)} className={`shrink-0 text-xs rounded-full px-2 py-1 flex items-center gap-1 ${p.i_prayed ? 'bg-amber-500 text-white' : 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300'}`} data-testid={`pray-btn-${p.id}`}>
                        🙏 {p.pray_count || 0}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {active && (active.pinned_messages || []).length > 0 && (
                <div className="bg-muted/60 border rounded-xl p-3 space-y-1.5" data-testid="pinned-messages">
                  <p className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5 uppercase tracking-wide"><Pin className="w-3.5 h-3.5" /> {t('messages.pinned')}</p>
                  {active.pinned_messages.map((p) => (
                    <div key={p.id} className="flex items-center gap-2">
                      <p className="text-sm flex-1 break-words">{p.text}</p>
                      {active.header?.is_admin && (
                        <button onClick={() => togglePin(p)} className="shrink-0 text-muted-foreground" data-testid={`unpin-btn-${p.id}`}><X className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!active ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : (
                active.messages.map((m, idx) => {
                  const mine = m.sender_id === currentUserId;
                  const seenDirect = mine && !isGroup && idx === myLastIdx && (m.read_by || []).includes(active.header?.other_user_id);
                  const seenGroup = mine && isGroup && idx === myLastIdx && (m.seen_by_count || 0) > 0;
                  if (m.kind === 'prayer') {
                    return (
                      <div key={m.id}>
                        <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className="max-w-[80%] rounded-2xl px-3 py-2 bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-800" data-testid={`prayer-msg-${m.id}`}>
                            {isGroup && !mine && <p className="text-[11px] font-semibold mb-0.5 opacity-80">{m.sender_name}</p>}
                            <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1"><HeartHandshake className="w-3 h-3" /> {t('messages.prayerRequest')}</p>
                            <p className="text-sm break-words">{m.text}</p>
                            {m.answered ? (
                              <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-green-700 dark:text-green-400" data-testid={`prayer-answered-badge-${m.id}`}><PartyPopper className="w-3.5 h-3.5" /> {t('messages.answeredBadge')}</span>
                            ) : (
                              <div className="flex items-center gap-2 mt-1">
                                <button onClick={() => pray(m.id)} className={`text-xs rounded-full px-2 py-0.5 flex items-center gap-1 ${m.i_prayed ? 'bg-amber-500 text-white' : 'bg-white/70 dark:bg-black/30 text-amber-800 dark:text-amber-300'}`} data-testid={`pray-btn-msg-${m.id}`}>
                                  🙏 {t('messages.praying')} · {m.pray_count || 0}
                                </button>
                                {mine && (
                                  <button onClick={() => markAnswered(m.id)} className="text-xs rounded-full px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400" data-testid={`mark-answered-${m.id}`}>{t('messages.answered')}</button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {seenDirect && <p className="text-[10px] text-muted-foreground text-right mt-0.5 pr-1" data-testid="messages-seen">{t('messages.seen')}</p>}
                        {seenGroup && <p className="text-[10px] text-muted-foreground text-right mt-0.5 pr-1" data-testid="messages-seen-group">{t('messages.seenBy', { count: m.seen_by_count })}</p>}
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className="group/msg">
                      <div className={`flex items-center gap-1 ${mine ? 'justify-end' : 'justify-start'}`}>
                        {isGroup && active?.header?.is_admin && !m.story_ref && (
                          <button onClick={() => togglePin(m)} className={`opacity-0 group-hover/msg:opacity-100 transition-opacity text-muted-foreground shrink-0 ${mine ? 'order-first' : 'order-last'}`} data-testid={`pin-btn-${m.id}`} title={m.pinned ? t('messages.unpin') : t('messages.pin')}>
                            <Pin className={`w-3.5 h-3.5 ${m.pinned ? 'fill-current text-primary' : ''}`} />
                          </button>
                        )}
                        <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                          {isGroup && !mine && <p className="text-[11px] font-semibold mb-0.5 opacity-80">{m.sender_name}</p>}
                          {m.story_ref && (
                            <div className="mb-1 rounded-lg overflow-hidden border border-white/20">
                              <img src={mediaUrl(m.story_ref.image_url)} alt="" className="w-28 h-28 object-cover" />
                              <p className="text-[10px] px-1.5 py-0.5 opacity-80">{t('messages.repliedToStory')}</p>
                            </div>
                          )}
                          <p className="text-sm break-words">{m.text}</p>
                        </div>
                      </div>
                      {seenDirect && <p className="text-[10px] text-muted-foreground text-right mt-0.5 pr-1" data-testid="messages-seen">{t('messages.seen')}</p>}
                      {seenGroup && <p className="text-[10px] text-muted-foreground text-right mt-0.5 pr-1" data-testid="messages-seen-group">{t('messages.seenBy', { count: m.seen_by_count })}</p>}
                    </div>
                  );
                })
              )}
              {typing.length > 0 && (
                <div className="flex justify-start" data-testid="messages-typing">
                  <div className="bg-muted rounded-2xl px-3 py-2 text-xs text-muted-foreground italic">
                    {typing.join(', ')} {t('messages.typing')}
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 border-t flex items-center gap-2">
              {isGroup && (
                <button onClick={() => setPrayerMode((v) => !v)} className={`p-2 rounded-full ${prayerMode ? 'bg-amber-500 text-white' : 'text-amber-600'}`} data-testid="messages-prayer-toggle" title={t('messages.prayerRequest')}>
                  <HeartHandshake className="w-5 h-5" />
                </button>
              )}
              <Input value={text} onChange={(e) => onType(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                placeholder={prayerMode ? t('messages.writePrayerRequest') : t('messages.typeMessage')} className="rounded-full" data-testid="messages-input" />
              <button onClick={send} disabled={sending || !text.trim()} className="text-primary p-2 disabled:opacity-40" data-testid="messages-send-btn">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
            </>
            )}
          </>
        )}
      </SheetContent>

      <NewGroupDialog open={groupOpen} onClose={() => setGroupOpen(false)} language={language} onCreated={(t2) => { setGroupOpen(false); loadLists(); openThread(t2.id); }} />
    </Sheet>
  );
};

export default MessagesInbox;
